import type { UserProfile } from '@pharmaconnect/types'
import type { Session, User } from '@supabase/supabase-js'
import { create } from 'zustand'

import { supabase } from '../lib/supabase'

// Rôles autorisés à se connecter au dashboard web pharmacien.
// Les médecins ont également accès (page /prescrire) — leur app
// principale est web pour la signature numérique.
const DASHBOARD_ROLES: ReadonlySet<UserProfile['role']> = new Set([
  'owner',
  'manager',
  'cashier',
  'super_admin',
  'doctor',
])

interface AuthState {
  session: Session | null
  user: User | null
  profile: UserProfile | null
  /** true tant que la session initiale n'a pas été résolue. */
  loading: boolean
  error: string | null
  /** AAL2 requis (TOTP enrollé) mais session encore en AAL1 → afficher MFA challenge. */
  mfaRequired: boolean
  bootstrap: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  loginCashier: (email: string, pin: string) => Promise<void>
  refreshAal: () => Promise<void>
  logout: () => Promise<void>
}

const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
  const { data, error } = await supabase.from('user_profiles').select('*').eq('id', userId).single()
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('Profile fetch error:', error.message)
    return null
  }
  return data as UserProfile
}

let listenerInstalled = false

export const useAuthStore = create<AuthState>()((set) => {
  const applySession = async (session: Session | null): Promise<void> => {
    if (!session) {
      set({ session: null, user: null, profile: null, error: null })
      return
    }
    const profile = await fetchProfile(session.user.id)
    if (!profile) {
      await supabase.auth.signOut()
      set({
        session: null,
        user: null,
        profile: null,
        error: 'Profil utilisateur introuvable. Contactez votre administrateur.',
      })
      return
    }
    if (!profile.is_active) {
      await supabase.auth.signOut()
      set({
        session: null,
        user: null,
        profile: null,
        error: 'Compte désactivé. Contactez votre administrateur.',
      })
      return
    }
    if (!DASHBOARD_ROLES.has(profile.role)) {
      await supabase.auth.signOut()
      set({
        session: null,
        user: null,
        profile: null,
        error: 'Ce compte n’a pas accès au dashboard pharmacien.',
      })
      return
    }
    // Vérifier si une élévation MFA est requise (TOTP enrollé mais session AAL1)
    let mfaRequired = false
    try {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      mfaRequired = aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2'
    } catch {
      // ignore — pas de MFA si l'API n'est pas dispo
    }
    set({ session, user: session.user, profile, error: null, mfaRequired })
  }

  return {
    session: null,
    user: null,
    profile: null,
    loading: true,
    error: null,
    mfaRequired: false,

    refreshAal: async () => {
      try {
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
        const required = aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2'
        set({ mfaRequired: required })
      } catch {
        set({ mfaRequired: false })
      }
    },

    bootstrap: async () => {
      if (!listenerInstalled) {
        listenerInstalled = true
        supabase.auth.onAuthStateChange((_event, session) => {
          void applySession(session)
        })
      }
      const {
        data: { session },
      } = await supabase.auth.getSession()
      await applySession(session)
      set({ loading: false })
    },

    login: async (email, password) => {
      set({ error: null })
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        set({ error: error.message })
        throw error
      }
      // applySession sera invoqué via onAuthStateChange.
    },

    loginCashier: async (email, pin) => {
      set({ error: null })
      const { data, error } = await supabase.functions.invoke<{
        token_hash: string
        email: string
      }>('cashier-login', { body: { email, pin } })

      if (error) {
        // FunctionsHttpError attache la réponse via `context` — on en extrait le message métier.
        const fallback = error.message ?? 'Connexion caissier impossible'
        const ctx = (error as { context?: Response }).context
        let message = fallback
        if (ctx && typeof ctx.text === 'function') {
          try {
            const text = await ctx.text()
            const parsed = JSON.parse(text) as { error?: { message?: string } }
            if (parsed?.error?.message) message = parsed.error.message
          } catch {
            // body non-JSON — on conserve le message générique
          }
        }
        set({ error: message })
        throw error
      }
      if (!data?.token_hash) {
        const e = new Error('Réponse caissier invalide')
        set({ error: e.message })
        throw e
      }

      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: data.token_hash,
        type: 'magiclink',
      })
      if (otpError) {
        set({ error: otpError.message })
        throw otpError
      }
      // applySession sera invoqué via onAuthStateChange.
    },

    logout: async () => {
      await supabase.auth.signOut()
      // applySession(null) sera invoqué via onAuthStateChange.
    },
  }
})

/** Sélecteur : true si l'utilisateur est authentifié avec un profil chargé. */
export const isAuthenticated = (state: AuthState): boolean =>
  Boolean(state.session && state.profile)
